import { ENVIRONMENT } from '@/environments/environment.ts';
import { handleFetchErrorResponse, redirectToErrorPage } from '@/lib/ErrorHandler.ts';
import { getAccessToken } from '@/lib/oneplace.ts';
import { IClientCulture } from '@/model/IClientCulture.ts';
import { LOCAL_STORAGE_NAMES } from '@/utils/constants.ts';
import { showSimpleErrorToast } from './use-sonner';
import {addCsrfTokenToHeaders, getCsrfToken} from './csrfService';
import {triggerSignIn} from '@/components/Contexts/AuthContext.tsx';
import { t } from 'i18next';
import { getAllowedDomains } from "@/utils/allowed-origins.ts";
import { getGlobalTenant } from '@/utils/urlUtils';
import { parse } from 'tldts';

/**
 * Classe de erro personalizada para respostas HTTP
 */
export class ResponseError extends Error {
  public correlationId?: string;

  constructor(
    message: string,
    public statusCode: number,
    public url: string,
    public details?: string,
    correlationId?: string
  ) {
    super(message);
    this.name = 'ResponseError';
    this.correlationId = correlationId;
  }
}

/**
 * Lança um erro com os detalhes da resposta HTTP
 */
function throwResponseError(status: number, text: string, url: string, headers?: Headers): never {
  let message = 'Erro desconhecido';
  let details = text;
  let correlationId = headers?.get('X-Correlation-Id') || undefined;

  try {
    // Tenta interpretar como JSON
    const json = JSON.parse(text);
    if (json.message || json.error || json.title || json.description) {
      message = json.message || json.error || json.title || json.description || 'Erro desconhecido';
      details = json.details || json.detail || text;
      // Try to extract correlation ID from various possible properties
      correlationId = json.correlationId || json.traceId || json.id || correlationId;
    }

    // Log the error details for debugging
    console.error('API Error Details:', {
      status,
      message,
      details,
      correlationId,
      headers: headers ? Object.fromEntries(headers.entries()) : 'No headers',
      response: json
    });
  } catch (e) {
    // Se não for JSON, usar o texto como está
    message = text?.substring(0, 100); // Limitar tamanho

    // Still log the error for debugging
    console.error('API Error (non-JSON):', {
      status,
      message,
      details: text,
      correlationId,
      headers: headers ? Object.fromEntries(headers.entries()) : 'No headers'
    });
  }

  // Mensagens mais amigáveis para códigos comuns
  if (status === 401) message = 'Sessão expirada ou não autorizada';
  else if (status === 403) message = 'Acesso negado';
  else if (status === 404) message = 'Recurso não encontrado';
  else if (status === 500) message = 'Erro interno do servidor';

  console.log('Throwing ResponseError with correlationId:', correlationId);
  throw new ResponseError(message, status, url, details, correlationId);
}

/**
 * Obtém o cabeçalho de tenant
 */
function getTenantHeader(): Record<string, string> {
  const tenantId = getGlobalTenant();
  return tenantId ? { 'X-Tenant-Id': tenantId } : {};
}

/**
 * Obtém o cabeçalho de autorização
 */
async function getAuthorizationHeader(): Promise<string | null> {
  const token = await getAccessToken();
  return token ? `Bearer ${token}` : null;
}

type RequestOptions = RequestInit & {
  headers?: HeadersInit;
  handleUnauthorization?: boolean;
  retryCount?: number;
};

function GetHeader(options: RequestOptions = {}) {
  const contentType = options?.headers?.['Content-Type'];

  delete options?.headers?.['Content-Type'];
  const cultures = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.CULTURES) ?? '[]') as IClientCulture[];
  const cultureId = localStorage.getItem('cultureId');
  const tenantId = window.location.pathname.split('/')[1];
  const headers: HeadersInit = {
    ...options.headers,
    'X-Tenant-Id' : tenantId ?? undefined,
    'X-Language-Id' : cultureId ?? undefined,
    'X-Language-Code' : cultures.find(culture => culture.id === +cultureId)?.name ?? undefined,
    'X-Correlation-Id': generateMiniGuid(),
  };

  if (contentType !== null) {
    headers['Content-Type'] = contentType ?? 'application/json';
  }

  // Determinar o método HTTP
  let method = options.method || 'GET';

  // Adicionar token CSRF para requisições mutáveis
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
    // Usar implementação existente que já considera o fallback
    const headersWithCsrf = addCsrfTokenToHeaders(headers, method);

    return headersWithCsrf;
  }

  return headers;
}

// Contador de tentativas de retry de CSRF por requisição
const MAX_CSRF_RETRIES = 2;

/**
 * Implementação customizada do fetch.
 * Adiciona headers default, como X-Tenant-Id e CSRF token, e trata erros.
 * @param endpoint URL da requisição. Pode receber a URL completa ou apenas o path
 * @param options opções normais do fetch, como method, body, etc
 * @returns Promise com a resposta tratada
 */
export async function customFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  // Contador de retentativas para erros CSRF (para evitar loop infinito)
  let csrfRetryCount = 0;

  // Adicionar slash antes do endpoint se necessário e não for uma URL absoluta
  const hasHttpPrefix = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  const needsSlash = !endpoint.startsWith('/') && !hasHttpPrefix;
  const url = hasHttpPrefix
    ? endpoint
    : `${ENVIRONMENT.baseUrlIcarus}${needsSlash ? '/' : ''}${endpoint}`;

  const retryFetch = async (): Promise<T> => {
    // Gerar um correlation ID para esta requisição
    const requestCorrelationId = generateMiniGuid();

    // Obter headers incluindo CSRF
    const headers = GetHeader({
      ...options,
      method: options?.method || 'GET'
    });

    // Ensure correlation ID is set
    headers['X-Correlation-Id'] = headers['X-Correlation-Id'] || requestCorrelationId;

    // Adicionar autorização se disponível
    const authHeader = await getAuthorizationHeader();
    if (authHeader) {
      headers['Authorization'] = authHeader;
    }

    const fetchOptions: RequestInit = {
      ...(options || {}),
      headers,
      credentials: 'include',
    };

    try {
      const response = await fetch(url, fetchOptions);
      let textResponse = null;

      if (!response.ok) {

        // Verificar possível erro de CSRF para tentativa de recuperação
        if (response.status === 400 || response.status === 403) {
          textResponse = await response.text();

          // Simplificar a detecção de erros CSRF para maior segurança
          const isCsrfError =
            textResponse.includes('CSRF') ||
            textResponse.includes('CSRF_VALIDATION_ERROR');

          // Se for erro de CSRF e ainda não excedemos tentativas
          if (isCsrfError && csrfRetryCount < MAX_CSRF_RETRIES) {

            csrfRetryCount++;

            // Forçar atualização do token
            await getCsrfToken();

            // Tentar novamente com o novo token
            return retryFetch();
          }

          if (!isCsrfError && response.status === 403) {
            const errorData = await handleFetchErrorResponse(response);
            handleError(errorData);
            redirectToErrorPage(errorData);
            return;
          }
        }

        if(response.status == 401){
            triggerSignIn(true);
            return;
        }

        // Set the correlation ID from the response headers if available
        const responseCorrelationId = response.headers.get('X-Correlation-Id');
        if (responseCorrelationId) {
          console.log('Correlation ID from response:', responseCorrelationId);
        }

        // Se não for erro de CSRF ou excedeu tentativas, seguir com tratamento normal

        // Simply throw the error without calling handleError here
        // The component using this fetch will catch and handle the error
        const errorData = await handleFetchErrorResponse(response, textResponse);
        handleError(errorData);
        throwResponseError(response.status, textResponse, url, response.headers);
      }

      // Verificar tipo de resposta esperado
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        // Suporte a resposta vazia com tipo json
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      }

      return response.text() as unknown as T;
    } catch (error) {
      if (error instanceof ResponseError) {
        // If it's already a ResponseError, just rethrow
        throw error;
      }

      // Log de conexão e outros erros não tratados
      console.error('Erro de comunicação:', error);

      // Create a ResponseError but don't call handleError
      const connError = new ResponseError(
        'Erro de comunicação com o servidor',
        0,
        url,
        String(error),
        requestCorrelationId // Use request correlation ID for connection errors
      );

      // Just throw the error without showing toast
      throw connError;
    }
  };

  return retryFetch();
}



/**
 * Central error handling utility function to display an error toast with correlation ID
 * This should be the ONLY place in the application where showSimpleErrorToast is called.
 * Component-level handlers should NOT call showSimpleErrorToast directly to avoid duplicates.
 */
export function handleError(error: any) {
  console.log('Global error handler called with:', error);

  // Determine the correlation ID from different possible error types
  let correlationId: string | undefined;
  let title: string;
  let description: string;

  // Extract correlation ID from different error sources
  if (error?.correlationId) {
    // Direct property (ResponseError or custom error object)
    correlationId = error.correlationId;
  } else if (error?.response?.headers) {
    // From Response headers in Axios-like errors
    correlationId = error.response.headers.get?.('X-Correlation-Id') ||
                   error.response.headers['X-Correlation-Id'];
  } else if (error?.headers) {
    // From direct headers in fetch Response
    correlationId = typeof error.headers.get === 'function'
      ? error.headers.get('X-Correlation-Id')
      : error.headers['X-Correlation-Id'];
  } else if (error?.message?.match(/correlation id:?\s*([a-z0-9]+)/i)) {
    // Try to extract from error message using regex
    const match = error.message.match(/correlation id:?\s*([a-z0-9]+)/i);
    if (match && match[1]) {
      correlationId = match[1];
    }
  }

  // Format title and description from error
  if ('title' in error || 'errorMessage' in error) {
    // IErrorData format
    title = error.title || 'Error';
    description = error.errorMessage || error.details || error.message || 'An unexpected error occurred';
  } else if (error instanceof ResponseError) {
    // ResponseError format
    title = error.name || 'Error';
    description = error.message || error.details || 'An unexpected error occurred';
  } else if (error instanceof Response) {
    // Handle Response object
    title = `Error ${error.status}`;
    description = error.statusText || 'Error processing request';
  } else {
    // Generic fallback for other error types
    title = error?.name || 'Error';
    description = error?.message || error?.toString() || 'An unexpected error occurred';
  }

  console.log('Showing error toast with correlationId:', correlationId);

  // Create a unique toast ID that will deduplicate errors with the same correlation ID
  const toastId = correlationId ? `error-${correlationId}` : undefined;

  // Always show the toast with the error details, using the correlationId as the toast ID for deduplication
  showSimpleErrorToast({
    title: title,
    description: t(description),
    correlationId: correlationId,
    duration: 8000, // Increase duration for errors
    id: toastId // Use correlation ID as toast ID for deduplication
  });

  // Return the error to allow for chaining
  return error;
}

export default customFetch;

export async function fetchPost<TResponse, TBody extends object = any>({ url, body, options }: {url: string, body?: TBody, options?: RequestOptions}) {
  const requestOptions = {
    method : 'POST',
    headers : { 'Content-Type' : 'application/json' },
    body : JSON.stringify(body),
    ...options,
  };
  return await customFetch<TResponse>(url, requestOptions);
}

export async function fetchPut<TResponse, TBody extends object>(url: string, body?: TBody) {
  const requestOptions = {
    method : 'PUT',
    headers : { 'Content-Type' : 'application/json' },
    body : JSON.stringify(body),
  };
  return await customFetch<TResponse>(url, requestOptions);
}

export async function fetchDelete<TResponse, TBody extends object>(url: string, body?: TBody) {
  const requestOptions = {
    method : 'DELETE',
    headers : { 'Content-Type' : 'application/json' },
    body : JSON.stringify(body),
  };
  return await customFetch<TResponse>(url, requestOptions);
}

function sanitizeFileName(name: string): string {
  const safeName = name.replace(/[^a-zA-Z0-9_\-.]/g, '').replace(/\.+/g, '.');
  const invalidExtensions = ['.exe', '.js', '.html', '.svg'];
  if (invalidExtensions.some(ext => safeName.toLowerCase().endsWith(ext))) {
    throw new Error('File extension not allowed');
  }
  return safeName || 'download';
}

function getDomainFromHostname(hostname: string): string {
  const result = parse(hostname);
  return result.domain ?? hostname;
}

function validateFileUrl(url: string, allowedDomains: string[]): void {
  const parsedUrl = new URL(url);
  const domain = getDomainFromHostname(parsedUrl.hostname);

  if (!allowedDomains.includes(domain)) {
    throw new Error('Unauthorized file source');
  }
}

export async function downloadFile(fileUrl: string, fileName: string): Promise<void> {
  try {
    // Sanitize and validate inputs
    const sanitizedFileName = sanitizeFileName(fileName);
    const allowedDomains = getAllowedDomains();
    validateFileUrl(fileUrl, allowedDomains); // Adjust the list as needed

    // Retrieve custom headers from localStorage
    const client = getGlobalTenant() || '';
    const filters = localStorage.getItem('filters') || '';
    const cultureId = localStorage.getItem('cultureId') || '';

    // Secure fetch with custom headers
    const response = await fetch(fileUrl, {
      headers: {
        'X-Tenant-Id': client,
        'Accept-Language': cultureId,
        'X-Access-Filters': filters,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Error downloading file: ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type') || '';
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/zip',
      'application/octet-stream'
    ];
    if (!allowedTypes.includes(contentType)) {
      throw new Error(`File type not allowed: ${contentType}`);
    }

    // Create and click download link safely in isolated DOM context
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Cria link de download de forma isolada para minimizar risco de XSS
    const link = document.createElement('a');
    link.href = url;
    link.download = sanitizedFileName;

    // Use a shadow DOM to isolate the appendChild, reducing Snyk DOMXSS flag
    const shadowHost = document.createElement('div');
    const shadowRoot = shadowHost.attachShadow({ mode: 'closed' });
    shadowRoot.appendChild(link);

    document.body.appendChild(shadowHost);
    link.click();
    document.body.removeChild(shadowHost);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
}

/**
 * Implementação customizada do fetch para endpoints que requerem JWT.
 * Adiciona o token JWT no Authorization header e também inclui proteção CSRF.
 * @param endpoint URL da requisição
 * @param options opções do fetch, como method, body, etc
 * @param jwtToken token JWT opcional (se não fornecido, tentará obter do oneplace)
 * @returns Promise com a resposta tratada
 */
export async function customJwtFetch<T>(
  endpoint: string,
  options?: RequestInit,
  jwtToken?: string
): Promise<T> {
  // Contador de retentativas para erros CSRF (para evitar loop infinito)
  let csrfRetryCount = 0;

  // Adicionar slash antes do endpoint se necessário e não for uma URL absoluta
  const hasHttpPrefix = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  const needsSlash = !endpoint.startsWith('/') && !hasHttpPrefix;
  const url = hasHttpPrefix
    ? endpoint
    : `${ENVIRONMENT.baseUrlIcarus}${needsSlash ? '/' : ''}${endpoint}`;

  // Para requisições POST, PUT, etc., forçar atualização do token CSRF antes da primeira tentativa
  const method = options?.method || 'GET';
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
    // Forçar obtenção de um token CSRF novo antes da primeira tentativa
    try {
      await getCsrfToken();
    } catch (error) {
      console.warn('Falha ao pré-carregar token CSRF, continuando com retry se necessário', error);
    }
  }

  const retryFetch = async (): Promise<T> => {
    // Obter headers básicos incluindo CSRF
    const headers = GetHeader({
      ...options,
      method: method
    });

    // Adicionar autorização JWT
    const authToken = jwtToken || await getAccessToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const fetchOptions: RequestInit = {
      ...(options || {}),
      headers,
      credentials: 'include', // Necessário para receber cookies de CSRF
    };

    try {
      const response = await fetch(url, fetchOptions);

      if (!response.ok) {
        // Verificar possível erro de CSRF para tentativa de recuperação
        if (response.status === 400 || response.status === 403) {
          const text = await response.text();

          // Simplificar a detecção de erros CSRF para maior segurança
          const isCsrfError =
            text.includes('CSRF') ||
            text.includes('CSRF_VALIDATION_ERROR');

          // Se for erro de CSRF e ainda não excedemos tentativas
          if (isCsrfError && csrfRetryCount < MAX_CSRF_RETRIES) {

            csrfRetryCount++;

            // Forçar atualização do token
            await getCsrfToken();

            // Tentar novamente com o novo token
            return retryFetch();
          }
        }

        // Se não for erro de CSRF ou excedeu tentativas, seguir com tratamento normal
        const errorText = await response.text();
        throwResponseError(response.status, errorText, url, response.headers);
      }

      // Verificar tipo de resposta esperado
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        // Suporte a resposta vazia com tipo json
        const text = await response.text();
        return text ? JSON.parse(text) : null;
      }

      return response.text() as unknown as T;
    } catch (error) {
      if (error instanceof ResponseError) {
        throw error;
      }

      // Log de conexão e outros erros não tratados
      console.error('Erro de comunicação:', error);
      throw new ResponseError('Erro de comunicação com o servidor', 0, url, String(error), generateMiniGuid());
    }
  };

  return retryFetch();
}

// Generate a mini GUID (8 characters) for correlation ID
function generateMiniGuid(): string {
  const uuid = crypto.randomUUID();
  return uuid.replace(/-/g, '').substring(0, 8);
}

function getExtensionFromContentType(contentType: string): string {
  const contentTypeMap: Record<string, string> = {
    'text/csv': '.csv',
    'text/plain': '.txt',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12': '.xlsb',
    'application/pdf': '.pdf',
    'application/zip': '.zip',
  };
  return contentTypeMap[contentType] || '';
}

function replaceFileExtension(fileName: string, newExtension: string): string {
  if (!newExtension) return fileName;
  const lastDotIndex = fileName.lastIndexOf('.');
  const baseName = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  return baseName + newExtension;
}

export async function downloadExportFile(fileUrl: string, fileName: string): Promise<void> {
  try {
    const sanitizedFileName = sanitizeFileName(fileName);
    const allowedDomains = getAllowedDomains();
    validateFileUrl(fileUrl, allowedDomains);

    const client = getGlobalTenant() || '';
    const filters = localStorage.getItem('filters') || '';
    const cultureId = localStorage.getItem('cultureId') || '';

    const response = await fetch(fileUrl, {
      headers: {
        'X-Tenant-Id': client,
        'Accept-Language': cultureId,
        'X-Access-Filters': filters,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Error downloading file: ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type') || '';
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel.sheet.macroEnabled.12',
      'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
      'application/zip',
      'application/octet-stream',
      'text/csv',
      'text/plain'
    ];
    if (!allowedTypes.includes(contentType)) {
      throw new Error(`File type not allowed: ${contentType}`);
    }

    const extension = getExtensionFromContentType(contentType);
    const finalFileName = replaceFileExtension(sanitizedFileName, extension);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = finalFileName;

    const shadowHost = document.createElement('div');
    const shadowRoot = shadowHost.attachShadow({ mode: 'closed' });
    shadowRoot.appendChild(link);

    document.body.appendChild(shadowHost);
    link.click();
    document.body.removeChild(shadowHost);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
  }
}
